// src/components/WebDevPromoModal/WebDevPromoModal.jsx
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  MonitorSmartphone,
  Rocket,
  Sparkles,
} from "lucide-react";
import NeonButton from "../ResponsiveButton/NeonButton.jsx";
import { useTheme } from "../../context/ThemeContext";

/**
 * WebDevPromoModal
 * --------------------------------------------------------------
 * Popup, jolla mainostat omia verkkosivupalveluja
 * (näytetään vain kirjautuneille käyttäjille).
 *
 * - `open`: ohjaa näkyvyyttä
 * - `onClose`: sulkee modaalin
 * - `onNeverShow`: "älä näytä enää" (esim. localStorage-lippu)
 */
export default function WebDevPromoModal({
  open,
  onClose,
  onNeverShow,
}) {
  const { theme } = useTheme();

  // Estä bodyä skrollaamasta, kun modali on auki
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Klikki CTA-nappiin → avaa arttustenius.com uuteen välilehteen
  const handleVisitSite = () => {
    try {
      window.open("https://arttustenius.com/", "_blank", "noopener,noreferrer");
    } catch (e) {
      // jos window ei ole saatavilla tms., ei kaadeta mitään
      console.error(e); // eslint-disable-line no-console
    }
  };

  // "Älä näytä enää" – käytä onNeverShow, jos annettu, muuten pelkkä close
  const handleNeverShow = () => {
    if (onNeverShow) {
      onNeverShow();
    } else if (onClose) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center pb-4 sm:pb-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Tausta / overlay */}
          <div
            className="absolute inset-0 z-0 bg-black/90"
            onClick={onClose}
            aria-hidden="true"
            role="presentation"
          />

          {/* Varsinainen dialogi */}
          <motion.div
            className={`
              relative z-[121]
              w-full sm:w-[90%]
              max-w-md sm:max-w-[640px]
              max-h-[78vh] sm:max-h-[92vh] overflow-y-auto
              rounded-t-3xl sm:rounded-2xl
              ring-1 backdrop-blur-md p-0
              ${
                theme === "dark"
                  ? "bg-night-700/80 ring-night-500/50"
                  : "bg-lemon/95 ring-black/10"
              }
            `}
            initial={{ y: 40, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 20, scale: 0.96, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="webdev-promo-title"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Sulje-nappi oikeaan ylänurkkaan */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Sulje"
              className="absolute top-2 right-2 sm:top-3 sm:right-3 inline-flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-black dark:text-white hover:bg-white/10"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>

            {/* Header + ingressi */}
            <div className="w-full px-5 pt-5 pr-14 sm:pr-16">
              {/* Pieni "pill"-teksti ylhäällä */}
              <div className="inline-flex items-center gap-2 rounded-full bg-pink/90 px-3 py-1 text-[11px] sm:text-xs text-white shadow-md shadow-pink/40">
                <Sparkles className="h-3.5 w-3.5" />
                <span>Tarvitsetko sivut bändille tai yritykselle?</span>
              </div>

              <h3
                id="webdev-promo-title"
                className="mt-3 font-customFont2 text-2xl sm:text-3xl font-bold text-black/85 dark:text-pink/90"
              >
                Teen Buukkerin tasoisia verkkosivuja myös sinulle.
              </h3>

              <p className="mt-2 font-customFont1 text-sm sm:text-base text-black/80 dark:text-white/80">
                Buukkerin rakensi Arttu Stenius – sama tyyppi voi tehdä
                selkeät, nopeat ja myyvät sivut myös sinun bändille,
                yritykselle tai tapahtumabrändille.
              </p>
            </div>

            {/* "Hyödyt" – kolmen kohdan grid */}
            <div className="px-5 pt-4 pb-3">
              <div className="grid gap-3 sm:grid-cols-3">
                {/* Kortti 1 */}
                <div className="rounded-2xl bg-white/80 dark:bg-white/5 border border-white/10 p-3 flex flex-col gap-2">
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-pink/80 text-white shadow">
                    <MonitorSmartphone className="h-4 w-4" />
                  </div>
                  <div className="text-sm font-semibold text-black/80 dark:text-white/90">
                    Moderni & mobiili
                  </div>
                  <p className="text-xs text-black/70 dark:text-white/70">
                    Sivut, jotka toimivat yhtä hyvin puhelimella kuin
                    läppärillä – ilman kömpelöitä valikoita.
                  </p>
                </div>

                {/* Kortti 2 */}
                <div className="rounded-2xl bg-white/80 dark:bg-white/5 border border-white/10 p-3 flex flex-col gap-2">
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/90 text-white shadow">
                    <ListChecksIcon />
                  </div>
                  <div className="text-sm font-semibold text-black/80 dark:text-white/90">
                    Myynti edellä
                  </div>
                  <p className="text-xs text-black/70 dark:text-white/70">
                    Selkeä rakenne, hinnat ja lomakkeet, jotka ohjaavat
                    kävijän yhteydenottoon – kuten Buukkerissa.
                  </p>
                </div>

                {/* Kortti 3 */}
                <div className="rounded-2xl bg-white/80 dark:bg-white/5 border border-white/10 p-3 flex flex-col gap-2">
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-500/90 text-white shadow">
                    <Rocket className="h-4 w-4" />
                  </div>
                  <div className="text-sm font-semibold text-black/80 dark:text-white/90">
                    Nopea toteutus
                  </div>
                  <p className="text-xs text-black/70 dark:text-white/70">
                    Pieni projekti voidaan julkaista usein jo
                    viikossa – sisältäen perus-SEO:n ja seurannan.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA-alue */}
            <div className="px-5 pb-4">
              <div className="rounded-2xl border border-white/15 bg-black/5 dark:bg-white/5 px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-sm font-semibold text-black/80 dark:text-white/85">
                    Kiinnostaisiko samantyylinen sivu kuin Buukkeri?
                  </div>
                  <p className="text-xs sm:text-sm text-black/70 dark:text-white/70">
                    Kurkkaa esimerkkisivut ja jätä lyhyt viesti, niin
                    katsotaan paljonko juuri sinun projektisi maksaisi.
                  </p>
                </div>

                <NeonButton
                  type="button"
                  variant="primary"
                  size="sm"
                  className="w-full sm:w-auto mt-1 sm:mt-0"
                  onClick={handleVisitSite}
                >
                  Katso portfolio
                </NeonButton>
              </div>

              <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex items-center gap-3 self-stretch sm:self-end justify-between sm:justify-end">
                  <button
                    type="button"
                    onClick={handleNeverShow}
                    className="text-[11px] sm:text-xs text-black/60 dark:text-white/60 underline underline-offset-2 hover:text-pink/80"
                  >
                    Älä näytä tätä enää
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Pieni apu-ikoni, ettei tarvitse importata koko ListChecks:ia joka paikassa
 */
function ListChecksIcon() {
  // lazy import olisi overkill, tehdään vaan alias
  const { ListChecks } = require("lucide-react"); // eslint-disable-line global-require
  return <ListChecks className="h-4 w-4" />;
}

WebDevPromoModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onNeverShow: PropTypes.func,
};

WebDevPromoModal.defaultProps = {
  open: false,
  onClose: undefined,
  onNeverShow: undefined,
};
